import * as I from 'immutable';
import React from 'react';

import {ApiFeatureData} from 'app/modules/Remote/Feature';
import {ApiFeatureCollection} from 'app/modules/Remote/FeatureCollection';
import {useFeatureData} from 'app/providers/FeatureDataProvider';
import {useContinuity, useStateWithDeps} from 'app/utils/hookUtils';
import {adjustImageRefs, resolveImageRefs} from 'app/utils/imageRefUtils';
import * as layers from 'app/utils/layers';
import * as mapUtils from 'app/utils/mapUtils';

export interface MapState {
  readonly mode: mapUtils.Mode;
  readonly imageRefs: mapUtils.MapImageRefs;
  readonly showFeatureIdInTitle: boolean;
}

export type MapStateAction =
  | {type: 'SET_MODE'; mode: mapUtils.Mode; imageRefIdxToKeep?: ImageRefIndex}
  /** Also updates the mode to match the number of refs. */
  | {type: 'SET_IMAGE_REFS'; imageRefs: mapUtils.MapImageRefs}
  /** Affects all current cursors. */
  | {type: 'SET_LAYER'; layerKey: string; imageRefIdx?: ImageRefIndex}
  | {type: 'TOGGLE_SHOW_FEATURE_ID_IN_TITLE'};

export type MapStateDispatch = (action: MapStateAction) => void;

export type ImageRefIndex = 0 | 1;

/**
 * Maintains the layer key, compare / view mode, and date cursors for the Lens
 * map. Provides a MapStateDispatch function that handles changing this state
 * via a set of defined actions.
 *
 * Cursors are adjusted to match the changing state. For example:
 *
 * - Changing from one layer to another should find cursors in the new layer.
 * - Changing from VIEW to COMPARE mode will add a new cursor before the current
 *   one (if possible, otherwise later).
 * - Changing between feature data, which most commonly happens when the user
 *   selects a new feature, will adjust the cursors once the feature data has
 *   loaded.
 *
 * In general, the cursors are synchronously adjusted when feature data is
 * available to match changes in layer, mode, or feature data. Nevertheless, the
 * returned cursors may not be found in the feature data while feature data is
 * loading or if the cursors were explicitly set to missing values. The latter
 * can occur when an old note references scenes that have since been deleted.
 *
 * Consumers will need to account for the fact that cursors are not guaranteed
 * to be found in the filtered feature data, and that cursors will only be
 * adjusted once feature data becomes available. Note as well that some UI
 * behavior (like not showing COMPARE mode for a multi-feature selection) needs
 * to be handled by UI components, not here.
 */
export function useMapState(
  featureCollection: I.ImmutableOf<ApiFeatureCollection>,
  featureIds: I.Set<number>,
  // For cases where we want to skip the default layer if a layer that should be present is in a note, alert, etc.
  initializeLayerKeyAsNone = false
): readonly [MapState, MapStateDispatch] {
  featureIds = useContinuity(featureIds, I.is);
  const {getFeatureData} = useFeatureData();

  // If there's more than one feature, we ignore the feature data for all of them.
  const featureDataMaybe = featureIds.size === 1 ? getFeatureData(featureIds.first()) : null;

  // Wait until we have some feature data to return it.
  const featureData = featureDataMaybe?.status === 'some' ? featureDataMaybe.value : null;

  const [mapState, setMapState] = useStateWithDeps<MapState>(
    {
      mode: mapUtils.MODES.VIEW,
      imageRefs: [
        {
          layerKey: initializeLayerKeyAsNone ? layers.NONE : layers.ANY_TRUECOLOR_HIGH_RES,
          cursor: null,
        },
      ],
      showFeatureIdInTitle: false,
    },
    [featureCollection.get('id')]
  );

  const mapStateDispatch = React.useCallback<MapStateDispatch>(
    (action) => {
      setMapState(applyUpdatesToState(action, featureData));
    },
    [setMapState, featureData]
  );

  // If feature data changes (e.g., a different feature is selected), update
  // map state with the adjusted cursors. This is done in the
  // `useLayoutEffect` hook to avoid UI flashing due to changing dates.
  React.useLayoutEffect(() => {
    setMapState((prevMapState) => {
      if (featureData === null) {
        return prevMapState;
      }

      const imageRefs = adjustImageRefs(prevMapState.imageRefs, prevMapState.mode, featureData);

      return {
        mode: imageRefs.length === 1 ? mapUtils.MODES.VIEW : mapUtils.MODES.COMPARE,
        imageRefs,
        showFeatureIdInTitle: prevMapState.showFeatureIdInTitle,
      };
    });
  }, [setMapState, featureData]);

  return [mapState, mapStateDispatch] as const;
}

/**
 * Applies the MapStateAction to the state.
 *
 * See the inline comments for notes on specific behavior, such as when we
 * adjust the cursors and when we don’t.
 */
export function applyUpdatesToState(
  action: MapStateAction,
  featureData: I.ImmutableOf<ApiFeatureData[]> | null
) {
  return (oldState: MapState): MapState => {
    switch (action.type) {
      case 'SET_MODE': {
        // Switching between view and compare. If going from compare -> view,
        // just sets the image to the later one in the list.
        //
        // Going from view to compare triggers a call to adjustImageRefs to find
        // a nearby image to compare to.
        const nextState = {
          ...oldState,
          mode: action.mode,
        };

        if (action.mode === oldState.mode) {
          return oldState;
        } else if (action.mode === mapUtils.MODES.VIEW) {
          // We assume that oldState’s imageRefs are already as correct as
          // they’re going to be, so no need to adjust or try to replace the
          // layerKey. Just collapse down to one ref.
          const [before, after] = oldState.imageRefs;
          // If we specify an imageRefIdx to keep, keep that imageRef. Provide the
          // first imageRef as a fallback, which is guaranteed to exist.
          if (action.imageRefIdxToKeep === 0 || action.imageRefIdxToKeep === 1) {
            nextState.imageRefs = [oldState.imageRefs[action.imageRefIdxToKeep] || before];
          } else {
            // If we don't specify an imageRef to keep, just keep the later one, but
            // provide a fallback value of the first imageRef, which is guaranteed to exist.
            nextState.imageRefs = [after || before];
          }
          return nextState;
        } else if (action.mode === mapUtils.MODES.COMPARE) {
          if (featureData !== null) {
            nextState.imageRefs = adjustImageRefs(oldState.imageRefs, nextState.mode, featureData);
          }
          return nextState;
        } else {
          throw new Error('Unknown mode: ' + action.mode);
        }
      }

      case 'SET_IMAGE_REFS': {
        // Refs coming from somewhere specific, such as a note or a link, are
        // reconciled against featureData (see resolveImageRefs). Mode follows
        // the number of refs provided.
        return {
          ...oldState,
          mode: action.imageRefs.length === 1 ? mapUtils.MODES.VIEW : mapUtils.MODES.COMPARE,
          imageRefs: resolveImageRefs(action.imageRefs, featureData),
        };
      }

      case 'SET_LAYER': {
        // Changes the imageRef (specified by index) to use the new layerKey,
        // then updates the cursors to find matching data from the new layer.

        // Once When we are in View mode, the imageRefIdx will always be 0. In
        // compare mode, the imageRefIdx will be 0 for start date, and 1
        // for End Date.

        let updatedImageRefs;
        // apply new layer to all imageRefs if 0 or 1 not specified
        if (!action.imageRefIdx && action.imageRefIdx !== 0) {
          updatedImageRefs = oldState.imageRefs.map((r) => ({
            ...r,
            layerKey: action.layerKey,
          })) as mapUtils.MapImageRefs;
        } else {
          oldState.imageRefs[action.imageRefIdx]!.layerKey = action.layerKey;
          updatedImageRefs = oldState.imageRefs;
        }

        return {
          ...oldState,

          imageRefs: featureData
            ? adjustImageRefs(updatedImageRefs, oldState.mode, featureData)
            : updatedImageRefs,
        };
      }

      case 'TOGGLE_SHOW_FEATURE_ID_IN_TITLE': {
        return {
          ...oldState,
          showFeatureIdInTitle: !oldState.showFeatureIdInTitle,
        };
      }
    }
  };
}
